/**
 * The white-label vocabulary layer — Step 79.
 *
 * A tenant renames the nouns: "ticket" becomes "case", "requester" becomes
 * "client", "agent" becomes "adviser". This is a first-class setting for a
 * white-label product rather than a nicety — a service desk sold to a law
 * firm that calls everything a "ticket" is visibly somebody else's product
 * with a logo swapped in.
 *
 * ── Overrides are PARTIAL, and that is the whole design ────────────────
 *
 * An org renames the two or three nouns it cares about and inherits the rest.
 * The alternative — storing a complete map per tenant — means every noun we
 * add later arrives missing for every existing tenant, and their UI grows
 * blanks on a deploy they had nothing to do with. So the stored map is a
 * sparse patch over `DEFAULT_VOCABULARY`, and an unknown key resolves to the
 * default rather than to nothing.
 *
 * ── Singular and plural are stored, never derived ──────────────────────
 *
 * No pluralisation rule survives contact with real words. English alone gives
 * "person/people" and "query/queries"; the reference ships explicit
 * singular/plural for the same reason. Deriving would produce "persons" on
 * somebody's customer-facing portal, which is exactly the polish this feature
 * exists to buy.
 *
 * ── Casing is presentational, so it is a function, not stored ──────────
 *
 * Storing "Ticket"/"ticket"/"TICKET" as three rows triples the editing
 * surface and lets them disagree. The stored value is the natural form a
 * person types; `titleCase` and `lowerFirst` adapt it at the render site.
 */

export interface VocabEntry {
  one: string;
  many: string;
}

/** The nouns that actually appear in this product's chrome. Deliberately
 *  bounded: every key here is an editing row somebody has to read, and a map
 *  of 137 mostly-irrelevant terms is a settings page nobody finishes. */
export const VOCABULARY_KEYS = [
  'ticket',
  'incident',
  'serviceRequest',
  'problem',
  'change',
  'task',
  'requester',
  'agent',
  'group',
  'catalogue',
  'catalogueItem',
  'article',
  'announcement',
  'approval',
] as const;

export type VocabularyKey = (typeof VOCABULARY_KEYS)[number];

export const DEFAULT_VOCABULARY: Record<VocabularyKey, VocabEntry> = {
  ticket: { one: 'ticket', many: 'tickets' },
  incident: { one: 'incident', many: 'incidents' },
  serviceRequest: { one: 'service request', many: 'service requests' },
  problem: { one: 'problem', many: 'problems' },
  change: { one: 'change', many: 'changes' },
  task: { one: 'task', many: 'tasks' },
  requester: { one: 'requester', many: 'requesters' },
  agent: { one: 'agent', many: 'agents' },
  group: { one: 'group', many: 'groups' },
  catalogue: { one: 'service catalogue', many: 'service catalogues' },
  catalogueItem: { one: 'service item', many: 'service items' },
  article: { one: 'article', many: 'articles' },
  announcement: { one: 'announcement', many: 'announcements' },
  approval: { one: 'approval', many: 'approvals' },
};

/** What a tenant has actually changed — sparse, and every field optional. */
export type VocabularyOverrides = Partial<Record<VocabularyKey, Partial<VocabEntry>>>;

/**
 * Resolve one noun.
 *
 * A blank override is treated as absent, not as an empty label: somebody
 * clearing a box means "go back to the default", and rendering "" would leave
 * a hole where a word should be with no way to tell it apart from a bug.
 */
export function vocab(overrides: VocabularyOverrides | null | undefined, key: VocabularyKey): VocabEntry {
  const base = DEFAULT_VOCABULARY[key];
  const over = overrides?.[key];
  if (!over) return base;
  return {
    one: over.one?.trim() || base.one,
    many: over.many?.trim() || base.many,
  };
}

/** Title case for the start of a sentence or a heading. Only the first
 *  letter — "Service request", never "Service Request", because the stored
 *  value may legitimately contain a proper noun the tenant capitalised. */
export function titleCase(word: string): string {
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : word;
}

/** The inverse, for mid-sentence use where the stored value was capitalised. */
export function lowerFirst(word: string): string {
  return word ? word.charAt(0).toLowerCase() + word.slice(1) : word;
}

/**
 * Narrow whatever came out of the database to a shape we will render.
 *
 * The column is JSON, so its contents are only ever a claim. Unknown keys are
 * dropped and non-string values ignored — a settings row edited by hand, or
 * left over from a key we renamed, must not put `[object Object]` into a
 * customer's portal.
 */
export function parseVocabulary(raw: unknown): VocabularyOverrides {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: VocabularyOverrides = {};
  for (const key of VOCABULARY_KEYS) {
    const value = (raw as Record<string, unknown>)[key];
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const { one, many } = value as Record<string, unknown>;
    const entry: Partial<VocabEntry> = {};
    if (typeof one === 'string' && one.trim()) entry.one = one.trim();
    if (typeof many === 'string' && many.trim()) entry.many = many.trim();
    if (entry.one || entry.many) out[key] = entry;
  }
  return out;
}
